import { type KeyboardEvent, useEffect, useState } from 'react'
import { useStrings } from '../i18n'
import { type ColorScheme, MAP_THEMES } from '../theme/palette'
import type { KeyboardTarget } from './viewport'

/**
 * The map, reachable without a pointer.
 *
 * MapLibre already gives the canvas arrow-key panning and +/- zoom once it has
 * focus. What it cannot give is a way to reach an individual site, so this is a
 * real listbox over what is currently in view: arrows move the cursor, Enter
 * selects, Escape clears the selection. It is off-screen until something in it
 * takes focus, then it becomes a visible card - the same trick a skip link
 * uses, so keyboard users get an affordance and nobody else gets clutter.
 */
export const MapKeyboardList = ({
  targets,
  selectedId,
  scheme,
  onSelect,
  onClear,
}: {
  targets: readonly KeyboardTarget[]
  selectedId: string | null
  scheme: ColorScheme
  onSelect: (siteId: string) => void
  onClear: () => void
}) => {
  const strings = useStrings()
  const theme = MAP_THEMES[scheme]
  const [activeIndex, setActiveIndex] = useState(0)

  // The list is rebuilt whenever the camera moves; the cursor must not point
  // past the end of the new one.
  useEffect(() => {
    setActiveIndex((index) => (index < targets.length ? index : 0))
  }, [targets])

  const active = targets[activeIndex]

  const onKeyDown = (event: KeyboardEvent<HTMLUListElement>): void => {
    if (targets.length === 0) return

    const move = (delta: number): void => {
      event.preventDefault()
      setActiveIndex((index) => Math.min(Math.max(index + delta, 0), targets.length - 1))
    }

    if (event.key === 'ArrowDown') {
      move(1)
      return
    }
    if (event.key === 'ArrowUp') {
      move(-1)
      return
    }
    if (event.key === 'PageDown') {
      move(10)
      return
    }
    if (event.key === 'PageUp') {
      move(-10)
      return
    }

    if (event.key === 'Home') {
      event.preventDefault()
      setActiveIndex(0)
      return
    }
    if (event.key === 'End') {
      event.preventDefault()
      setActiveIndex(targets.length - 1)
      return
    }
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      if (active !== undefined) onSelect(active.id)
      return
    }
    if (event.key === 'Escape') {
      event.preventDefault()
      onClear()
    }
  }

  return (
    <div className="map__keyboard">
      <p className="map__keyboard-title">{strings.map.keyboardTitle}</p>
      <p className="map__keyboard-hint">{strings.map.keyboardHint}</p>

      {targets.length === 0 ? (
        <p className="map__keyboard-empty">{strings.map.keyboardEmpty}</p>
      ) : (
        <ul
          className="map__keyboard-list"
          // biome-ignore lint/a11y/noNoninteractiveElementToInteractiveRole: a single-select listbox over up to 40 rows is exactly what this role describes; the alternative, 40 tab stops, is worse for the keyboard users it exists for.
          role="listbox"
          tabIndex={0}
          aria-label={strings.map.keyboardList}
          aria-activedescendant={active === undefined ? undefined : `map-kbd-${active.id}`}
          onKeyDown={onKeyDown}
        >
          {targets.map((target, index) => (
            // biome-ignore lint/a11y/useFocusableInteractive: options in an aria-activedescendant listbox are deliberately not focusable - focus stays on the list and the active option is announced through the attribute.
            // biome-ignore lint/a11y/useKeyWithClickEvents: keyboard handling lives on the listbox, per the same pattern; the click handler is for pointer users only.
            <li
              key={target.id}
              id={`map-kbd-${target.id}`}
              // biome-ignore lint/a11y/noNoninteractiveElementToInteractiveRole: see the listbox above; these are its options.
              role="option"
              aria-selected={target.id === selectedId}
              data-active={index === activeIndex}
              className="map__keyboard-option"
              onClick={() => onSelect(target.id)}
            >
              <span
                aria-hidden="true"
                className="map__keyboard-swatch"
                style={{ background: theme.band[target.band] }}
              />
              <span className="map__keyboard-name">{target.name}</span>
              <span className="map__keyboard-meta">
                {target.city} · {target.pmax} {strings.filters.minPowerUnit}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
